import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
export class FetchError {
    constructor({ status, response, code, errno, url, parsedResponseBody }) {
        this.status = status;
        this.code = code;
        this.errno = errno;
        this.response = response;
        this.url = url;
        this.parsedResponseBody = parsedResponseBody;
    }
}
export const isTimeoutError = (rawError) => rawError.code === 'ECONNABORTED' && rawError.errno === 'ETIME';
export class BaseError extends Error {
    constructor() {
        super(...arguments);
        this.parsedErrors = undefined;
    }
}
export class ClientError extends BaseError {
    constructor(raw, message) {
        super(message || 'ClientError');
        if (raw.status !== 500 && raw.status !== 502) {
            try {
                this.parsedErrors = JSON.parse(raw?.parsedResponseBody || '{}');
            }
            catch (err) {
                console.error('expected JSON got ', raw?.parsedResponseBody);
            }
        }
        const toBackendError = (e) => typeof e === 'string' ? { message: e } : { message: e.message, code: e.weenatCode || e.code };
        this.status = raw.status;
        const parsedErrorsCasted = this.parsedErrors ?? {};
        this.nonFieldErrors = !Array.isArray(parsedErrorsCasted)
            ? parsedErrorsCasted.weenatCode
                ? [parsedErrorsCasted]
                : 'non_field_errors' in parsedErrorsCasted
                    ? (parsedErrorsCasted.non_field_errors || [])
                        .concat(parsedErrorsCasted.detail ? [parsedErrorsCasted.detail] : [])
                        .map(toBackendError)
                    : []
            : parsedErrorsCasted.map(toBackendError);
        this.fieldErrors = this.fieldErrors = !Array.isArray(parsedErrorsCasted)
            ? Object.entries(parsedErrorsCasted)
                .filter(([key]) => key !== 'non_field_errors' && key !== 'detail')
                .reduce((acc, [key, errors]) => {
                let newEntry;
                if (Array.isArray(errors)) {
                    newEntry = { [key]: errors.map(toBackendError) };
                }
                else {
                    newEntry = {
                        [key]: {
                            ...Object.entries(errors).reduce((acc, [key, value]) => ({
                                ...acc,
                                [key]: Array.isArray(value) ? value.map(toBackendError) : value
                            }), {})
                        }
                    };
                }
                return {
                    ...acc,
                    ...newEntry
                };
            }, {})
            : undefined;
    }
}
export class ClientTimeoutError extends ClientError {
    constructor(raw) {
        super(raw, 'ClientTimeoutError');
    }
}
export class ClientBadRequestError extends ClientError {
    constructor(raw) {
        super(raw, `ClientBadRequestError body:${raw.parsedResponseBody}`);
    }
}
export class ClientAuthenticationError extends ClientError {
    constructor(raw) {
        super(raw, 'ClientAuthenticationError');
    }
}
export class ClientAuthorizationError extends ClientError {
    constructor(raw) {
        super(raw, 'ClientAuthorizationError');
    }
}
export class ClientNotFoundError extends ClientError {
    constructor(raw) {
        super(raw, `ClientNotFoundError url was ${raw.url}`);
    }
}
export class ClientRateLimitError extends ClientError {
    constructor(raw) {
        super(raw, 'ClientRateLimitError');
    }
}
export class ClientInternalServerError extends ClientError {
    constructor(raw) {
        super(raw, 'ClientInternalServerError');
    }
}
export class ClientConnectionError extends ClientError {
    constructor(raw) {
        super(raw, 'ClientConnectionError');
    }
}
export class ClientBadVerbError extends ClientError {
    constructor(raw) {
        super(raw, `ClientBadVerbError body:${raw.parsedResponseBody}`);
    }
}
export class ArvalisError extends BaseError {
    constructor(body) {
        const arvalisError = JSON.parse(body);
        const msg = arvalisError.error_reporting || arvalisError.message || arvalisError.error;
        super(msg);
        const parsedStatusCode = parseInt(arvalisError.error, 10);
        this.httpStatusCode = isNaN(parsedStatusCode) ? arvalisError.statusCode : parsedStatusCode;
        this.message = msg;
    }
}
export const handleError = async (error) => {
    let body = '';
    try {
        body =
            (error.response?.body &&
                error.response?.body.text &&
                typeof error.response?.body.text === 'function' &&
                (await error.response?.body.text())) ||
                '';
        error.parsedResponseBody = body;
    }
    catch (e) {
        // body wil be empty error will not be as specific but we don't want to return here
    }
    if (error.url?.includes('/ts-workers') && error.url?.includes('arvalis')) {
        return !isNil(error.parsedResponseBody) && !isEmpty(error.parsedResponseBody)
            ? new ArvalisError(error.parsedResponseBody)
            : new BaseError('Empty error from arvalis');
    }
    if (isTimeoutError(error))
        return new ClientTimeoutError(error);
    switch (error?.status) {
        case 400:
            return new ClientBadRequestError(error);
        case 401:
        case 403:
            return new ClientAuthenticationError(error);
        case 404:
            return new ClientNotFoundError(error);
        case 405:
            return new ClientBadVerbError(error);
        case 429:
            return new ClientRateLimitError(error);
        case 500:
            return new ClientInternalServerError(error);
        case 503:
        case 502:
            return new ClientConnectionError(error);
        default:
            return new BaseError(error);
    }
};
